/**
 * Compose the collapsed Closed rail's vertical label (SE-6862).
 *
 * The rail is 44px wide, so its label is rotated a quarter turn and reads as
 * one continuous line: "Closed · Signed 4 · Passed 8" (Figma node 80:18287).
 * Building it as a single string rather than as a row of elements is what makes
 * the rotation tractable — a rotated element keeps its pre-transform layout box,
 * and one text node has exactly one box to reason about.
 * @module features/player-kanban/utils/closedRailLabel
 */

import type { ClosedGroupData } from "../types/types";

/** Leading word of the rail label, shown even when nothing is closed yet. */
const RAIL_PREFIX = "Closed";

/** Separator between the prefix and each outcome summary. */
const RAIL_SEPARATOR = " · ";

/**
 * Build the rail's one-line summary of what is closed.
 * @remarks
 * Group order is preserved exactly as given — `generateClosedGroups` has
 * already ordered the groups by the board template's `validOutcomes`, and the
 * rail must not re-sort them or it would disagree with the expanded panel
 * directly beside it.
 * @param groups - Closed groups, pre-ordered by `validOutcomes`.
 * @returns The rail label, e.g. `Closed · Signed 4 · Passed 8`.
 */
export const closedRailLabel = (groups: readonly ClosedGroupData[]): string =>
  [RAIL_PREFIX, ...groups.map(group => `${group.label} ${group.count}`)].join(
    RAIL_SEPARATOR
  );
